import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectChairperson,
  selectChairpersonLogins,
  signInChairperson
} from '../redux/workSlice';
import PinEntry from '../components/PinEntry';

/**
 * The door to the chairperson screen: nobody gets past it without a
 * chairperson's PIN.
 *
 * Always, whatever the competition's login mode says. That setting chooses how
 * judges sign in; a chairperson has no list to pick from, because the screen
 * behind this door controls the timer and reads every panel's marks, and
 * because knowing *which* chairperson holds the device is what lets the
 * timetable show them their own categories.
 *
 * The PIN is matched here, against the officials list marX broadcasts - the
 * same trust model as the adjudicator PIN (Adjudicator.tsx). Once in, the
 * device stays signed in across reloads and reconnects until the chairperson
 * logs out on the home screen or their PIN changes on the desktop
 * (updateJudgesList). Nothing is announced to the server on the way in: the
 * screen behind the door does that when it mounts.
 */
const ChairpersonGate: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chairperson = useAppSelector(selectChairperson);
  const logins = useAppSelector(selectChairpersonLogins);

  if (chairperson) {
    return <>{children}</>;
  }

  const handleLogin = (pinCode: string): string | null => {
    const match = logins.find((official) => official.pinCode === pinCode);
    if (!match) {
      return t('adjudicator.pin.invalidPin') as string;
    }
    dispatch(
      signInChairperson({ uuid: match.uuid, name: match.name, pinCode })
    );
    return null;
  };

  return (
    <PinEntry
      title={t('chairperson.pin.title') as string}
      onSubmit={handleLogin}
      // No chairperson PIN on this device yet: typing one cannot work, so say
      // what is missing rather than answer every attempt with "invalid PIN".
      notice={
        logins.length === 0 ? (t('chairperson.pin.noPins') as string) : null
      }
    />
  );
};

export default ChairpersonGate;
